import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type SellerPermissionCatalogue, type SellerStaffRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

/**
 * The people who work here.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * One password, shared by everybody. Which was survivable while the system only
 * recorded sales, and became untenable the moment it started recording a count
 * coming up eight packs short — because "HealthPlus Wuse II" is all four people
 * on the rota, and therefore none of them. Shrinkage you can see but cannot
 * attribute is a number that worries a pharmacist and changes nothing.
 *
 * ── ORDINARY WORK, THEN DELIBERATE EXTRAS ───────────────────────────────────
 *
 * A new account gets the ordinary day and nothing else. Anything that touches
 * money, or destroys stock, needs a written reason — kept with the owner's name
 * against it and readable here, not buried in a log only ChiCare can open.
 *
 * ── THE THREE THAT ARE NOT ON THIS SCREEN ───────────────────────────────────
 *
 * Bank details, the business record, and this screen itself. They are not
 * missing by oversight: there is no permission for them anywhere in the system,
 * so no sequence of careless ticks ends with a locum able to repoint where the
 * settlements land. That is said out loud at the bottom, because a limit nobody
 * knows about gets worked around.
 */
export function SellerStaffScreen() {
  const [editing, setEditing] = useState<SellerStaffRow | 'new' | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['seller', 'staff'],
    queryFn: ({ signal }) => api.sellerStaff(signal),
  });

  if (isError) {
    return <AdminError title="We couldn't load your team" error={error} onRetry={() => refetch()} />;
  }

  if (editing) {
    return (
      <StaffForm
        staff={editing === 'new' ? null : editing}
        catalogue={data?.catalogue ?? {}}
        onDone={() => setEditing(null)}
      />
    );
  }

  const staff = data?.staff ?? [];

  return (
    <div className="mx-auto w-full max-w-[860px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Your team</h1>
          <p className="m-0 mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
            Give each person their own login. Every delivery booked in, every count and every
            write-off then carries a name — which is the only way a shortage ever gets explained.
          </p>
        </div>

        <Button onClick={() => setEditing('new')}>Add someone</Button>
      </header>

      {data?.owner?.name ? (
        <div className="mb-4 flex items-start gap-2.5 rounded-card border border-line bg-white px-4 py-3">
          <Icon name="shield" size={15} filled className="mt-px shrink-0 text-verified" />
          <p className="m-0 text-[12.5px] leading-snug text-muted">
            <b className="text-ink">{data.owner.name}</b> owns this business and can do everything,
            including the bank details. That cannot be given to anybody else.
          </p>
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-[76px] w-full" radius="card" />
          ))}
        </div>
      ) : staff.length === 0 ? (
        <EmptyState
          title="Nobody else has a login yet"
          body="While everyone shares one password, the stock record cannot say who did what — and a count that comes up short has nobody's name on it."
          action={<Button onClick={() => setEditing('new')}>Add someone</Button>}
        />
      ) : (
        <div className="overflow-hidden rounded-card border border-line bg-white">
          {staff.map((one) => (
            <StaffRow key={one.id} staff={one} onEdit={() => setEditing(one)} />
          ))}
        </div>
      )}

      <p className="m-0 mt-4 text-[11.5px] leading-relaxed text-faint">
        Nobody on this screen can ever reach your bank details, change your business record, or add
        people. Those stay with you — there is no permission for them, so they cannot be given away
        by mistake.
      </p>
    </div>
  );
}

function StaffRow({ staff, onEdit }: { staff: SellerStaffRow; onEdit: () => void }) {
  const gone = staff.status !== 'active';

  return (
    <button
      type="button"
      onClick={onEdit}
      data-testid={`staff-${staff.id}`}
      className="flex w-full min-h-11 items-start gap-3 border-b border-line bg-white px-4 py-3 text-left last:border-b-0 hover:bg-paper"
    >
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className={cn('text-[14px] font-bold', gone ? 'text-muted' : 'text-ink')}>
            {staff.name}
          </span>
          {staff.title ? (
            <span className="rounded-pill bg-paper px-2 py-[2px] text-[10.5px] font-medium text-muted">
              {staff.title}
            </span>
          ) : null}
          {gone ? (
            <span className="rounded-pill bg-danger-tint px-2 py-[2px] text-[10.5px] font-bold text-danger">
              Login closed
            </span>
          ) : null}
        </span>

        <span className="mt-0.5 block break-all text-[12.5px] text-muted">{staff.email}</span>

        <span className="mt-1 block font-mono text-[11px] text-faint">
          {staff.permissions.length} thing{staff.permissions.length === 1 ? '' : 's'} they may do
          {staff.elevated.length > 0
            ? ` · ${staff.elevated.length} extra privilege${staff.elevated.length === 1 ? '' : 's'}`
            : ''}
        </span>
      </span>

      <Icon name="chevron" size={16} strokeWidth={2} className="mt-1 shrink-0 text-muted" />
    </button>
  );
}

function StaffForm({
  staff,
  catalogue,
  onDone,
}: {
  staff: SellerStaffRow | null;
  catalogue: SellerPermissionCatalogue;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(staff?.name ?? '');
  const [email, setEmail] = useState(staff?.email ?? '');
  const [phone, setPhone] = useState(staff?.phone ?? '');
  const [password, setPassword] = useState('');
  const [title, setTitle] = useState(staff?.title ?? '');
  const [status, setStatus] = useState(staff?.status ?? 'active');
  const [note, setNote] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // Everything ordinary, ticked, for somebody new. The extras start off.
  const ordinary = Object.values(catalogue)
    .flat()
    .filter((one) => !one.elevated)
    .map((one) => one.value);

  const [permissions, setPermissions] = useState<string[]>(staff?.permissions ?? ordinary);

  const before = staff?.permissions ?? [];
  const elevatedNow = Object.values(catalogue)
    .flat()
    .filter((one) => one.elevated && permissions.includes(one.value) && !before.includes(one.value));

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['seller', 'staff'] });
    onDone();
  };

  const save = useMutation({
    mutationFn: () =>
      staff
        ? api.updateSellerStaff(staff.id, {
            name,
            title,
            permissions,
            status,
            ...(password === '' ? {} : { password }),
            ...(note.trim() === '' ? {} : { note: note.trim() }),
          })
        : api.createSellerStaff({
            name,
            email,
            phone,
            password,
            title,
            permissions,
            ...(note.trim() === '' ? {} : { note: note.trim() }),
          }),
    onSuccess: done,
  });

  const remove = useMutation({
    mutationFn: () => api.revokeSellerStaff(staff!.id),
    onSuccess: done,
  });

  const error = [save.error, remove.error].find((e): e is ApiError => e instanceof ApiError);

  const toggle = (value: string) =>
    setPermissions((current) =>
      current.includes(value) ? current.filter((one) => one !== value) : [...current, value],
    );

  const incomplete =
    name.trim() === '' ||
    (!staff && (email.trim() === '' || password.length < 10)) ||
    (elevatedNow.length > 0 && note.trim() === '');

  return (
    <div className="mx-auto w-full max-w-[640px] px-4 py-6 xl:px-8">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-semibold text-muted"
      >
        <Icon name="back" size={15} strokeWidth={2} />
        Your team
      </button>

      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        {staff ? staff.name : 'Someone new'}
      </h1>

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="Their name"
          value={name}
          placeholder="Chidi Okeke"
          error={error?.fieldErrors.name?.[0] ?? null}
          onChange={(event) => setName(event.target.value)}
        />

        <Input
          label="What they do here"
          value={title}
          placeholder="Counter assistant"
          hint="Their job title, in your words."
          onChange={(event) => setTitle(event.target.value)}
        />

        {!staff ? (
          <>
            <Input
              label="Email they sign in with"
              type="email"
              value={email}
              placeholder="chidi@yourshop.ng"
              error={error?.fieldErrors.email?.[0] ?? null}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Input
              label="Phone (optional)"
              mono
              value={phone}
              placeholder="0803 123 4567"
              onChange={(event) => setPhone(event.target.value)}
            />
          </>
        ) : null}

        <Input
          label={staff ? 'New password' : 'Password'}
          type="password"
          value={password}
          autoComplete="new-password"
          error={error?.fieldErrors.password?.[0] ?? null}
          hint={
            staff
              ? 'Leave empty to keep theirs. Changing it signs them out everywhere.'
              : 'At least 10 characters. Tell it to them yourself — ChiCare never emails passwords.'
          }
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>

      <p className="m-0 mt-5 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        What they may do
      </p>

      <div className="mt-2 space-y-3">
        {Object.entries(catalogue).map(([group, items]) => (
          <section key={group} className="overflow-hidden rounded-card border border-line bg-white">
            <p className="m-0 border-b border-line px-4 py-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
              {group}
            </p>

            {items.map((item) => {
              const on = permissions.includes(item.value);

              return (
                <label
                  key={item.value}
                  className={cn(
                    'flex min-h-11 cursor-pointer items-start gap-2.5 border-b border-line-2 px-4 py-2.5 last:border-b-0',
                    on && item.elevated
                      ? 'bg-caution-tint/50'
                      : on
                        ? 'bg-blue-tint/40'
                        : 'bg-white',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggle(item.value)}
                    data-testid={`sell-perm-${item.value}`}
                    className="mt-0.5 h-4 w-4 shrink-0"
                  />

                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[13px] font-medium text-ink">{item.label}</span>
                      {item.elevated ? (
                        <span className="rounded-pill bg-caution-tint px-1.5 py-[1px] font-mono text-[9px] uppercase tracking-[0.08em] text-caution-ink">
                          Extra
                        </span>
                      ) : null}
                    </span>

                    {item.warning ? (
                      <span className="mt-0.5 flex items-start gap-1.5 text-[11.5px] leading-snug text-caution-ink">
                        <Icon name="alert" size={12} strokeWidth={2} className="mt-px shrink-0" />
                        {item.warning}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </section>
        ))}
      </div>

      {/* The note is only demanded when something extra is being handed over —
          and taking a privilege away never needs one, because making somebody's
          access smaller should be the easy direction. */}
      {elevatedNow.length > 0 ? (
        <div
          data-testid="staff-note-required"
          className="mt-4 rounded-card border border-caution bg-caution-tint px-4 py-3.5"
        >
          <p className="m-0 text-[12.5px] font-bold text-caution-ink">
            You are giving {name || 'this person'}{' '}
            {elevatedNow.map((one) => one.label.toLowerCase()).join(' and ')}.
          </p>
          <label className="mt-2 block">
            <span className="mb-1 block text-[12px] font-semibold text-caution-ink">
              Why do they need it?
            </span>
            <textarea
              value={note}
              rows={2}
              data-testid="staff-note"
              onChange={(event) => setNote(event.target.value)}
              placeholder="She reconciles the till at close of business."
              className="w-full rounded-inp border border-caution bg-white px-3 py-2 text-[13px] leading-relaxed text-ink outline-none focus:border-blue"
            />
          </label>
          <p className="m-0 mt-1 text-[11px] leading-snug text-caution-ink/80">
            Kept with your name against it, on this screen, permanently.
          </p>
        </div>
      ) : null}

      {/* The history. Not an audit log somebody else owns — theirs to read. */}
      {staff && staff.grants.length > 0 ? (
        <div className="mt-5">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            Extra privileges, and who gave them
          </p>
          <div className="mt-2 space-y-1.5">
            {staff.grants.map((grant, i) => (
              <div
                key={i}
                className={cn(
                  'rounded-card border px-3.5 py-2.5',
                  grant.is_live ? 'border-line bg-white' : 'border-line bg-paper',
                )}
              >
                <p className="m-0 text-[12.5px] font-semibold text-ink">
                  {grant.label}
                  {!grant.is_live ? (
                    <span className="ml-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-faint">
                      taken back
                    </span>
                  ) : null}
                </p>
                <p className="m-0 mt-0.5 text-[12px] leading-snug text-muted">“{grant.note}”</p>
                <p className="m-0 mt-0.5 text-[11px] text-faint">
                  {grant.granted_by}
                  {grant.granted_at
                    ? ` · ${new Date(grant.granted_at).toLocaleDateString('en-NG')}`
                    : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {staff ? (
        <label className="mt-4 flex min-h-11 cursor-pointer items-start gap-2.5 rounded-card border border-line bg-white px-4 py-3">
          <input
            type="checkbox"
            checked={status === 'active'}
            onChange={(event) => setStatus(event.target.checked ? 'active' : 'suspended')}
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-medium text-ink">Can sign in</span>
            <span className="mt-0.5 block text-[11.5px] leading-snug text-muted">
              Turn this off for somebody on leave, without losing what they did.
            </span>
          </span>
        </label>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="m-0 mt-4 rounded-card border border-danger bg-danger-tint px-3.5 py-2.5 text-[12.5px] font-medium text-danger"
        >
          {error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button disabled={incomplete || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : staff ? 'Save changes' : 'Create their login'}
        </Button>

        <Button variant="grey" onClick={onDone}>
          Cancel
        </Button>

        {staff ? (
          <span className="ml-auto">
            {confirmingRemove ? (
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-[12.5px] text-muted">Close their login?</span>
                <Button
                  size="sm"
                  variant="grey"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  {remove.isPending ? 'Closing…' : 'Close it'}
                </Button>
                <button
                  type="button"
                  onClick={() => setConfirmingRemove(false)}
                  className="min-h-11 bg-transparent px-1 text-[12.5px] font-semibold text-muted"
                >
                  Keep it
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingRemove(true)}
                className="min-h-11 bg-transparent px-1 text-[12.5px] font-semibold text-muted"
              >
                Close their login
              </button>
            )}
          </span>
        ) : null}
      </div>

      {staff ? (
        <p className="m-0 mt-3 text-[11.5px] leading-relaxed text-faint">
          Closing a login signs them out everywhere and stops the password working. Their name stays
          on everything they did — a stock count with nobody against it is worth nothing.
        </p>
      ) : null}
    </div>
  );
}
